/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/spinner';
import { OrderListBody } from '@/components/account/order-list';
import { useLoadMoreOrders } from '@/hooks/use-load-more-orders';
import { DEFAULT_ORDERS_LIMIT } from '@/lib/order/pagination';
import type { CustomerOrdersPage } from '@/lib/api/order.server';

/**
 * The initial order-history window resolved by the route loader, plus the derived visible order count.
 *
 * @property visibleTotal - Total visible (non-failed) orders for the progress label, or `null` when it
 *   couldn't be derived (the failed-order count fetch failed). See the order-history loader.
 */
export type OrderHistoryInitial = CustomerOrdersPage & {
    visibleTotal: number | null;
};

/**
 * "View more" button for the order-history list. Mirrors the product-grid "Load more" control: a
 * progress line lives in the list footer (see {@link OrderListBody}); this renders the error/retry
 * message and the button, and stays hidden once every order has loaded.
 */
function ViewMoreOrdersButton({
    isLoading,
    hasError,
    describedById,
    onClick,
}: {
    isLoading: boolean;
    hasError: boolean;
    describedById: string;
    onClick: () => void;
}): ReactElement {
    const { t } = useTranslation('account');

    return (
        <div data-slot="orders-view-more" className="flex flex-col items-center gap-3 pt-4">
            {hasError && (
                <p data-slot="orders-view-more-error" role="alert" className="text-sm text-destructive">
                    {t('orders.viewMoreError')}
                </p>
            )}
            <Button
                type="button"
                variant="outline"
                size="lg"
                // Full-width with a 48px tap target on mobile; centered fixed-width on larger viewports.
                className="h-12 w-full min-w-56 sm:h-11 sm:w-auto"
                onClick={onClick}
                disabled={isLoading}
                aria-describedby={describedById}
                data-slot="orders-view-more-button">
                {isLoading && <Spinner size="sm" aria-hidden="true" />}
                {isLoading ? t('orders.viewMoreLoading') : hasError ? t('orders.viewMoreRetry') : t('orders.viewMore')}
            </Button>
        </div>
    );
}

/**
 * Order-history list with a "View more" control. Rendered inside the route's `<Await>`; receives the
 * loader's first window and streams further windows client-side via {@link useLoadMoreOrders}.
 */
export function OrderHistoryView({
    initial,
    onViewDetails,
}: {
    initial: OrderHistoryInitial;
    onViewDetails?: (orderNo: string) => void;
}): ReactElement {
    const countId = useId();
    const { orders, loadedCount, total, canLoadMore, isLoading, hasError, loadMore } = useLoadMoreOrders({
        initialOrders: initial.orders,
        initialNextOffset: initial.nextOffset,
        initialHasMore: initial.hasMore,
        limit: DEFAULT_ORDERS_LIMIT,
        total: initial.visibleTotal,
    });

    return (
        <>
            <OrderListBody
                orders={orders}
                total={total}
                loadedCount={loadedCount}
                countId={countId}
                onViewDetails={onViewDetails}
            />
            {canLoadMore && (
                <ViewMoreOrdersButton
                    isLoading={isLoading}
                    hasError={hasError}
                    describedById={countId}
                    onClick={loadMore}
                />
            )}
        </>
    );
}

export default OrderHistoryView;
